import {
  Button,
  Input,
  Modal,
  SearchBar,
  Select,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import type { User } from "../../../api/userManagement.service";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { companies, type Company } from "../../../api/addressBook.service";
import {
  type FirmFeatureStates,
  userManagement,
  type Organisation,
  type OrganisationMember,
  type OrganisationDomain,
  type OrganisationInvite,
} from "../../../api/userManagement.service";
import { CompanyPicker } from "../../../components/CompanyPicker/CompanyPicker";
import { DataTable } from "../../../components/DataTable/DataTable";
import LoadingState from "../../../components/LoadingState";
import EmptyState from "../../../components/EmptyState";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal";
import { formatDateTimeShort } from "../../../utils/dateFormatter";
import { ROUTES } from "../../../routes";
import { FirmEntitlementSection } from "./FirmEntitlementSection";
import { FulfilmentProductsSection } from "./FulfilmentProductsSection";
import { CxiReportSection } from "./CxiReportSection";
import styles from "../UserManagement.module.css";

type OrganisationDetailContext = {
  setToast?: (toast: { message: string; type: ToastType } | null) => void;
};

type AddMemberModalProps = {
  orgId: string;
  existingMemberIds: Set<string>;
  onMemberAdded: () => Promise<void>;
  onClose: () => void;
  onToast: (message: string, type: ToastType) => void;
};

function AddMemberModal({
  orgId,
  existingMemberIds,
  onMemberAdded,
  onClose,
  onToast,
}: AddMemberModalProps) {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearchChange(value: string) {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  }

  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setSearchResults([]);
      return;
    }
    void fetchMatchingUsers(debouncedSearch);
  }, [debouncedSearch]);

  async function fetchMatchingUsers(query: string) {
    setSearching(true);
    try {
      const result = await userManagement.getUsers({
        search: query,
        context: "account",
        limit: 10,
      });
      setSearchResults(result.users.filter(foundUser => !existingMemberIds.has(foundUser.id)));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function handleSelectUser(selectedUser: User) {
    setAdding(true);
    try {
      await userManagement.addOrganisationMember(orgId, selectedUser.id);
      await onMemberAdded();
      onClose();
      onToast(`${selectedUser.name} added to organisation`, "success");
    } catch (err) {
      onToast(
        `Failed to add member: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setAdding(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Add member"
      size="small"
      blurBackground
      backdropClick
    >
      <div style={{ padding: "1rem" }}>
        <SearchBar
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search users by name or email…"
        />
        {searching && (
          <p
            style={{
              color: "var(--color-text-secondary)",
              fontSize: "var(--font-size-sm)",
              marginTop: "0.75rem",
            }}
          >
            Searching…
          </p>
        )}
        {!searching && searchResults.length === 0 && debouncedSearch.trim() && (
          <p
            style={{
              color: "var(--color-text-secondary)",
              fontSize: "var(--font-size-sm)",
              marginTop: "0.75rem",
            }}
          >
            No users found.
          </p>
        )}
        {searchResults.length > 0 && (
          <ul style={{ listStyle: "none", padding: 0, margin: "0.75rem 0 0" }}>
            {searchResults.map(resultUser => (
              <li
                key={resultUser.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0.5rem 0",
                  borderBottom: "1px solid var(--color-border)",
                }}
              >
                <div>
                  <div style={{ fontWeight: "var(--font-weight-medium)" }}>{resultUser.name}</div>
                  <div
                    style={{
                      color: "var(--color-text-secondary)",
                      fontSize: "var(--font-size-sm)",
                    }}
                  >
                    {resultUser.email}
                  </div>
                </div>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => void handleSelectUser(resultUser)}
                  disabled={adding}
                >
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

type EditOrganisationModalProps = {
  org: Organisation;
  onClose: () => void;
  onSaved: (updated: Organisation) => void;
  onToast: (message: string, type: ToastType) => void;
};

function EditOrganisationModal({ org, onClose, onSaved, onToast }: EditOrganisationModalProps) {
  const [name, setName] = useState(org.name);
  const [status, setStatus] = useState<"active" | "inactive">(
    org.status === "inactive" ? "inactive" : "active",
  );
  const [company, setCompany] = useState<Company | null>(null);
  const [companyLoading, setCompanyLoading] = useState(Boolean(org.companyId));
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!org.companyId) return;
    let cancelled = false;
    setCompanyLoading(true);
    companies
      .getById(org.companyId)
      .then(response => {
        if (!cancelled) setCompany(response.data);
      })
      .catch(error => {
        // The linked company may have been removed; leave the picker empty.
        console.error(`Failed to load linked company ${org.companyId}:`, error);
      })
      .finally(() => {
        if (!cancelled) setCompanyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [org.companyId]);

  async function handleSave() {
    if (saving) return;

    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setNameError("Name is required");
      return;
    }

    // Only send fields that actually changed; companyId is nullable so a cleared
    // link is sent explicitly as null.
    const payload: { name?: string; status?: "active" | "inactive"; companyId?: string | null } =
      {};
    if (trimmedName !== org.name) payload.name = trimmedName;
    if (status !== org.status) payload.status = status;
    const nextCompanyId = company?.id ?? null;
    if (nextCompanyId !== (org.companyId ?? null)) payload.companyId = nextCompanyId;

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const updated = await userManagement.updateOrganisation(org.id, payload);
      onSaved(updated);
    } catch (err) {
      onToast(err instanceof Error ? err.message : "Failed to update organisation", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen title="Edit organisation" onClose={onClose} size="small">
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <Input
          label="Name"
          value={name}
          onChange={event => {
            setName(event.target.value);
            setNameError(undefined);
          }}
          error={nameError}
          disabled={saving}
          required
          fullWidth
          autoComplete="off"
        />

        <Select
          label="Status"
          value={status}
          onChange={event => setStatus(event.target.value === "inactive" ? "inactive" : "active")}
          disabled={saving}
          options={[
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
          ]}
        />

        <CompanyPicker
          label="Company (optional)"
          value={company}
          onChange={setCompany}
          disabled={saving || companyLoading}
          placeholder={companyLoading ? "Loading current company…" : undefined}
        />

        <div className={styles.formActions}>
          <Button
            variant="outlined"
            color="default"
            type="button"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function OrganisationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const context = useOutletContext<OrganisationDetailContext>();

  const [org, setOrg] = useState<Organisation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [members, setMembers] = useState<OrganisationMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [memberSearch, setMemberSearch] = useState("");
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState("");
  const memberSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [domains, setDomains] = useState<OrganisationDomain[]>([]);
  const [domainsLoading, setDomainsLoading] = useState(true);

  const [invites, setInvites] = useState<OrganisationInvite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(true);

  const [inviteToRevoke, setInviteToRevoke] = useState<OrganisationInvite | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const [memberToRemove, setMemberToRemove] = useState<OrganisationMember | null>(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [managedModeConfirmTarget, setManagedModeConfirmTarget] = useState<
    "auto" | "manual" | null
  >(null);
  const [savingManagedMode, setSavingManagedMode] = useState(false);

  const [featureStates, setFeatureStates] = useState<FirmFeatureStates | null>(null);
  const entitlementSource = featureStates?.source ?? "current";

  useEffect(() => {
    if (id) {
      loadOrg(id);
      loadDomains(id);
      loadInvites(id);
      loadFeatureStates(id);
    }
  }, [id]);

  async function loadFeatureStates(orgId: string) {
    try {
      setFeatureStates(await userManagement.getOrganisationFeatureStates(orgId));
    } catch {
      setFeatureStates(null);
    }
  }

  useEffect(() => {
    if (id) {
      loadMembers(id, debouncedMemberSearch);
    }
  }, [id, debouncedMemberSearch]);

  async function loadOrg(orgId: string) {
    try {
      setLoading(true);
      setError(null);
      const data = await userManagement.getOrganisationById(orgId);
      setOrg(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load organisation"));
    } finally {
      setLoading(false);
    }
  }

  function handleMemberSearchChange(value: string) {
    setMemberSearch(value);
    if (memberSearchDebounceRef.current) clearTimeout(memberSearchDebounceRef.current);
    memberSearchDebounceRef.current = setTimeout(() => {
      setDebouncedMemberSearch(value);
    }, 300);
  }

  async function loadMembers(orgId: string, search?: string) {
    try {
      setMembersLoading(true);
      const data = await userManagement.getOrganisationMembers(orgId, search || undefined);
      setMembers(data);
    } catch (err) {
      showToast(
        `Failed to load members: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setMembersLoading(false);
    }
  }

  async function loadDomains(orgId: string) {
    try {
      setDomainsLoading(true);
      const data = await userManagement.getOrganisationDomains(orgId);
      setDomains(data);
    } catch (err) {
      showToast(
        `Failed to load domains: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setDomainsLoading(false);
    }
  }

  async function loadInvites(orgId: string) {
    try {
      setInvitesLoading(true);
      const data = await userManagement.getOrganisationInvites(orgId);
      setInvites(data);
    } catch (err) {
      showToast(
        `Failed to load invites: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setInvitesLoading(false);
    }
  }

  function showToast(message: string, type: ToastType) {
    setToast({ message, type });
    context.setToast?.({ message, type });
  }

  async function handleRevokeInvite() {
    if (!id || !inviteToRevoke) return;
    try {
      await userManagement.revokeInvite(id, inviteToRevoke.id);
      setInviteToRevoke(null);
      await loadInvites(id);
      showToast("Invite revoked", "success");
    } catch (err) {
      showToast(
        `Failed to revoke invite: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
      setInviteToRevoke(null);
    }
  }

  async function handleConfirmManagedModeChange() {
    if (!id || !managedModeConfirmTarget) return;
    const targetMode = managedModeConfirmTarget;
    setSavingManagedMode(true);
    try {
      await userManagement.setOrganisationManagedMode(id, targetMode);
      setManagedModeConfirmTarget(null);
      await loadOrg(id);
      showToast(
        targetMode === "manual"
          ? "Firm is now manually managed"
          : "Firm returned to automatic management",
        "success",
      );
    } catch (err) {
      showToast(
        `Failed to update managed mode: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
      setManagedModeConfirmTarget(null);
    } finally {
      setSavingManagedMode(false);
    }
  }

  async function handleRemoveMember() {
    if (!id || !memberToRemove) return;
    await userManagement.removeOrganisationMember(id, memberToRemove.id);
    const removedMember = memberToRemove;
    setMemberToRemove(null);
    await loadMembers(id, debouncedMemberSearch);
    showToast(`${removedMember.name} removed from organisation`, "success");
  }

  const memberColumns: Column<OrganisationMember>[] = [
    { header: "Name", accessor: "name" },
    { header: "Email", accessor: "email" },
    {
      header: "Roles",
      accessor: "roles",
      render: (_value: unknown, member: OrganisationMember) =>
        member.roles.length > 0 ? member.roles.join(", ") : "—",
    },
    {
      header: "Joined",
      accessor: "joinedAt",
      render: (_value: unknown, member: OrganisationMember) => formatDateTimeShort(member.joinedAt),
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_value: unknown, member: OrganisationMember) => (
        <PermissionGate permission="user.write_accounts">
          <Button
            variant="outlined"
            color="error"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setMemberToRemove(member);
            }}
          >
            Remove
          </Button>
        </PermissionGate>
      ),
    },
  ];

  const inviteColumns: Column<OrganisationInvite>[] = [
    { header: "Email", accessor: "email" },
    { header: "Status", accessor: "status" },
    {
      header: "Created",
      accessor: "createdAt",
      render: (_value: unknown, invite: OrganisationInvite) =>
        formatDateTimeShort(invite.createdAt),
    },
    {
      header: "Expires",
      accessor: "expiresAt",
      render: (_value: unknown, invite: OrganisationInvite) =>
        invite.expiresAt ? formatDateTimeShort(invite.expiresAt) : "Never",
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_value: unknown, invite: OrganisationInvite) => (
        <PermissionGate permission="user.write_accounts">
          <Button
            variant="outlined"
            color="default"
            onClick={e => {
              e.stopPropagation();
              setInviteToRevoke(invite);
            }}
          >
            Revoke
          </Button>
        </PermissionGate>
      ),
    },
  ];

  if (loading) return <LoadingState />;

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) return <UnauthorizedView />;
    return (
      <EmptyState
        variant="error"
        title="Failed to load"
        message={`Failed to load organisation: ${error.message}`}
      />
    );
  }

  if (!org)
    return <EmptyState variant="empty" title="Not found" message="Organisation not found" />;

  return (
    <div className={styles.detailWrapper}>
      {entitlementSource === "current" && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Entitlement overview</h2>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "0.5rem",
            }}
          >
            <span
              className={`${styles.statusBadge} ${
                org.managedMode === "manual" ? styles.statusManual : styles.statusAuto
              }`}
            >
              {org.managedMode === "manual" ? "Manually managed" : "Auto-managed (data mart)"}
            </span>
            <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
              {org.managedMode === "manual"
                ? "legacy_metadata is the sole source of truth; data-mart ignored"
                : "data-mart is base; legacy_metadata adds/overrides per product"}
            </span>
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <h3
              style={{
                fontSize: "var(--font-size-base)",
                fontWeight: "var(--font-weight-medium)",
                marginBottom: "0.5rem",
              }}
            >
              legacy_metadata content
            </h3>
            {!org.legacyMetadata ||
            (Array.isArray((org.legacyMetadata as { permissions?: unknown }).permissions) &&
              (org.legacyMetadata as { permissions: unknown[] }).permissions.length === 0 &&
              !("data_capabilities" in org.legacyMetadata) &&
              !("submission_capabilities" in org.legacyMetadata) &&
              !("restrict_publications_to" in org.legacyMetadata)) ? (
              <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
                No legacy_metadata set for this firm.
              </p>
            ) : (
              <div style={{ fontSize: "var(--font-size-sm)" }}>
                {Array.isArray((org.legacyMetadata as { permissions?: unknown }).permissions) &&
                  (
                    org.legacyMetadata as {
                      permissions: { product: string; publication_ids?: number[] }[];
                    }
                  ).permissions.length > 0 && (
                    <div style={{ marginBottom: "0.5rem" }}>
                      <strong>Products: </strong>
                      {(
                        org.legacyMetadata as {
                          permissions: { product: string; publication_ids?: number[] }[];
                        }
                      ).permissions
                        .map(
                          perm =>
                            `${perm.product}${perm.publication_ids?.length ? ` [${perm.publication_ids.join(", ")}]` : ""}`,
                        )
                        .join(", ")}
                    </div>
                  )}
                {"data_capabilities" in (org.legacyMetadata ?? {}) && (
                  <div>
                    <strong>data_capabilities:</strong> enabled
                  </div>
                )}
                {"submission_capabilities" in (org.legacyMetadata ?? {}) && (
                  <div>
                    <strong>submission_capabilities:</strong> enabled
                  </div>
                )}
                {"restrict_publications_to" in (org.legacyMetadata ?? {}) && (
                  <div>
                    <strong>restrict_publications_to:</strong>{" "}
                    {(
                      org.legacyMetadata as { restrict_publications_to: number[] }
                    ).restrict_publications_to?.join(", ")}
                  </div>
                )}
                <details style={{ marginTop: "0.5rem" }}>
                  <summary
                    style={{
                      cursor: "pointer",
                      color: "var(--color-text-secondary)",
                      fontSize: "var(--font-size-xs, 0.75rem)",
                    }}
                  >
                    Raw JSON
                  </summary>
                  <pre
                    style={{
                      fontSize: "var(--font-size-xs, 0.75rem)",
                      background: "var(--color-bg-secondary, #f3f4f6)",
                      padding: "0.5rem",
                      borderRadius: "4px",
                      overflowX: "auto",
                      marginTop: "0.25rem",
                    }}
                  >
                    {JSON.stringify(org.legacyMetadata, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>

          <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
            {/* TODO: add GET /api/v1/organisations/:orgId/datamart-coverage endpoint for per-firm data-mart summary */}
            Data-mart coverage and UPE summary: view individual member pages for per-user breakdown.
            Frontegg-cache parity: visible on individual member pages via the Source attribution
            panel.
          </p>
        </div>
      )}

      <div className={styles.section}>
        <h2
          className={styles.sectionTitle}
          style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}
        >
          Organisation Info
          {entitlementSource === "current" && (
            <span
              className={`${styles.statusBadge} ${
                org.managedMode === "manual" ? styles.statusManual : styles.statusAuto
              }`}
            >
              {org.managedMode === "manual" ? "Manually managed" : "Auto-managed (data mart)"}
            </span>
          )}
        </h2>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Name</span>
            <span className={styles.infoValue}>{org.name}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Status</span>
            <span className={styles.infoValue}>{org.status}</span>
          </div>
          {org.companyId && (
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Company ID</span>
              <span className={styles.infoValue}>{org.companyId}</span>
            </div>
          )}
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Created</span>
            <span className={styles.infoValue}>{formatDateTimeShort(org.createdAt)}</span>
          </div>
        </div>
        <PermissionGate permission="user.write_accounts">
          <div style={{ marginTop: "1rem", display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <Button variant="outlined" color="default" onClick={() => setShowEditModal(true)}>
              Edit details
            </Button>
            {entitlementSource === "current" &&
              (org.managedMode === "manual" ? (
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => setManagedModeConfirmTarget("auto")}
                  disabled={savingManagedMode}
                >
                  Release to auto
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => setManagedModeConfirmTarget("manual")}
                  disabled={savingManagedMode}
                >
                  Take manual control
                </Button>
              ))}
          </div>
        </PermissionGate>
        <PermissionGate permission="staff.marketing.diagnostics">
          <div style={{ marginTop: "1rem", display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="default"
              onClick={() =>
                navigate(
                  `${ROUTES.marketingToolkit.firmDiagnostics}?organisationId=${encodeURIComponent(org.id)}`,
                )
              }
            >
              Check Marketing Toolkit access
            </Button>
          </div>
        </PermissionGate>
      </div>

      {entitlementSource === "fulfilment" ? (
        <FulfilmentProductsSection
          temporalFeatures={featureStates?.temporalFeatures ?? []}
          companyId={org.companyId ?? ""}
        />
      ) : (
        <FirmEntitlementSection orgId={id!} />
      )}

      <CxiReportSection orgId={id!} />

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
            Members
          </h2>
          <PermissionGate permission="user.write_accounts">
            <Button variant="outlined" color="default" onClick={() => setShowAddMemberModal(true)}>
              Add member
            </Button>
          </PermissionGate>
        </div>
        <div className={styles.searchSection}>
          <SearchBar
            value={memberSearch}
            onChange={handleMemberSearchChange}
            placeholder="Search members…"
          />
        </div>
        {membersLoading ? (
          <LoadingState />
        ) : (
          <DataTable
            data={members}
            columns={memberColumns}
            emptyMessage="No members in this organisation."
            rowKey="id"
            onRowClick={member => navigate(ROUTES.users.detail(member.id))}
          />
        )}
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Domains</h2>
        {domainsLoading ? (
          <LoadingState />
        ) : domains.length === 0 ? (
          <p style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)" }}>
            No domains configured.
          </p>
        ) : (
          <div className={styles.domainList}>
            {domains.map(domain => (
              <div key={domain.id} className={styles.domainItem}>
                {domain.domain}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Pending Invites</h2>
        {invitesLoading ? (
          <LoadingState />
        ) : (
          <DataTable
            data={invites}
            columns={inviteColumns}
            emptyMessage="No pending invites."
            rowKey="id"
          />
        )}
      </div>

      {showEditModal && (
        <EditOrganisationModal
          org={org}
          onClose={() => setShowEditModal(false)}
          onToast={showToast}
          onSaved={updated => {
            setOrg(updated);
            setShowEditModal(false);
            showToast("Organisation updated", "success");
          }}
        />
      )}

      {managedModeConfirmTarget === "manual" && (
        <ConfirmationModal
          title="Take manual control"
          message="This firm will be managed manually. Its current entitlements are snapshotted into the firm record and staff edits become authoritative — the data mart will no longer override them. Continue?"
          warningMessage="The data mart will be ignored for this firm until manual control is released."
          confirmButtonText="Take control"
          onClose={() => setManagedModeConfirmTarget(null)}
          onConfirm={handleConfirmManagedModeChange}
        />
      )}

      {managedModeConfirmTarget === "auto" && (
        <ConfirmationModal
          title="Release to auto"
          message="This firm will return to automatic (data-mart) management. The data mart will drive entitlements again and override staff edits. Continue?"
          warningMessage="Staff edits to this firm will no longer be authoritative once released."
          confirmButtonText="Release"
          onClose={() => setManagedModeConfirmTarget(null)}
          onConfirm={handleConfirmManagedModeChange}
        />
      )}

      {inviteToRevoke && (
        <ConfirmationModal
          title="Revoke Invite"
          message={`Are you sure you want to revoke the invite for "${inviteToRevoke.email}"?`}
          warningMessage="The invited user will no longer be able to use this invite link."
          confirmButtonText="Revoke"
          confirmButtonColor="error"
          onClose={() => setInviteToRevoke(null)}
          onConfirm={handleRevokeInvite}
        />
      )}

      {memberToRemove && (
        <ConfirmationModal
          title="Remove member"
          message={`Remove "${memberToRemove.name}" from this organisation?`}
          warningMessage="The user will lose access and their roles in this firm will be deleted."
          confirmButtonText="Remove"
          confirmButtonColor="error"
          onClose={() => setMemberToRemove(null)}
          onConfirm={handleRemoveMember}
        />
      )}

      {showAddMemberModal && id && (
        <AddMemberModal
          orgId={id}
          existingMemberIds={new Set(members.map(member => member.id))}
          onMemberAdded={async () => {
            await loadMembers(id, debouncedMemberSearch);
          }}
          onClose={() => setShowAddMemberModal(false)}
          onToast={showToast}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default OrganisationDetail;
